/**
 * useConstructionDataFetching Hook
 * 
 * Custom React hook for fetching construction worker salary data from the backend API.
 * Provides data fetching functionality with loading and error state management.
 * 
 * Features:
 * - Initial data fetch on mount (all records)
 * - Filtered data fetching with optional parameters (year, month, site/genba, employee name)
 * - Loading state management
 * - Error handling and display
 * - Dynamic query parameter building
 * - Uses authenticatedFetch for API calls
 * 
 * Note: Construction workers use different fields than office employees:
 * - Site (現場/genbaName) instead of department
 * - Employee name instead of employee code
 * - No separate salary/bonus types in the salary table; 現場賞与 uses construction-bonus APIs
 * 
 * Used by:
 * - ConstructionDataDisplay component for construction worker data display
 * 
 * @module useConstructionDataFetching
 */

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../../../config/apiConfig';
import { authenticatedFetch } from '../../../../../utils/authenticatedFetch';
import { CONSTRUCTION_BONUS_API_BASE_PATH } from '../../../../../utils/constructionBonusContract';

/**
 * Interface defining the structure of construction worker salary data.
 */
interface ConstructionSalaryData {
  genbaName: string;                        // Site name (現場名)
  employeeName: string;                     // Employee name (従業員名)
  year: number;                             // Year (年)
  month: number;                            // Month (月, 1-12)
  attendanceDays: number;                   // Attendance days (出勤日数)
  totalWage: number;                        // Total wage (賃金総額)
  healthInsurance: number;                  // Health insurance (健康保険)
  employmentInsurance: number;              // Employment insurance (雇用保険)
  pensionInsurance: number;                 // Pension insurance (厚生年金)
  incomeTax: number;                        // Income tax (所得税)
  fixedReduction: number;                   // Fixed reduction (定額減税)
  residentTax: number;                      // Resident tax (住民税)
  legalDeductionTotal: number;              // Legal deduction total (法定控除合計)
  deductedPayment1: number;                 // Deducted payment 1 (差引支給額1)
  mealCost: number;                         // Meal cost (食事代)
  miscellaneousExpenses: number;            // Miscellaneous expenses (雑費)
  gasolineCost: number;                     // Gasoline cost (ガソリン代)
  advancePayment: number;                   // Advance payment (前渡金)
  beddingCost: number;                      // Bedding cost (寝具代)
  leaseCost: number;                        // Lease cost (リース代)
  agreementDeductionTotal: number;          // Agreement deduction total (協定控除合計)
  totalDeductions: number;                  // Total deductions (控除合計)
  deductedPayment2: number;                 // Deducted payment 2 (差引支給額2)
  otherPayment: number;                     // その他支給
  otherDeduction: number;                   // その他控除
  yearEndRefund: number;                    // 年末調整還付金
  transferAmount: number;                   // Transfer amount (振込金額)
  otherPaymentBreakdown?: string;          // その他支給内訳
  otherDeductionBreakdown?: string;         // その他控除内訳
  remarks?: string;                        // 備考
  paidLeaveDays: number;                    // Paid leave days (有給日数)
  remainingPaidLeave: number;               // Remaining paid leave (残有給)
  overtimeHours: number;                    // Overtime hours (残業時間)
  baseSalary: number;                       // Base salary (基本給)
  pieceRatePay: number;                     // Piece rate pay (出来高給)
  siteAllowance: number;                    // Site allowance (現場手当)
  overtimeAllowance: number;                // Overtime allowance (残業手当)
  nightShiftAllowance: number;              // Night shift allowance (夜勤手当)
  holidayAllowance: number;                 // Holiday allowance (休日手当)
  otherAllowance?: number;                  // Other allowance (その他手当)
  incomeTax2: number;                       // Income tax 2 (所得税2)
  // Daily attendance data (day1 to day31)
  day1: number;
  day2: number;
  day3: number;
  day4: number;
  day5: number;
  day6: number;
  day7: number;
  day8: number;
  day9: number;
  day10: number;
  day11: number;
  day12: number;
  day13: number;
  day14: number;
  day15: number;
  day16: number;
  day17: number;
  day18: number;
  day19: number;
  day20: number;
  day21: number;
  day22: number;
  day23: number;
  day24: number;
  day25: number;
  day26: number;
  day27: number;
  day28: number;
  day29: number;
  day30: number;
  day31: number;
}

/**
 * useConstructionDataFetching Hook
 * 
 * Custom hook for fetching construction worker salary data.
 * Fetches data from Spring Boot backend API using authenticatedFetch.
 * 
 * @returns Object containing data, loading, error, and fetchFilteredData function
 * 
 * @example
 * ```typescript
 * const { data, loading, error, fetchFilteredData } = useConstructionDataFetching();
 * ```
 */
export const useConstructionDataFetching = (dataType: 'salary' | 'bonus' = 'salary') => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const fetchFilteredData = useCallback(async (
    year?: string,
    month?: string,
    genbaName?: string,
    employeeName?: string
  ) => {
    setLoading(true);
    setError('');

    try {
      const apiBase = dataType === 'bonus'
        ? `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}`
        : `${API_BASE_URL}/construction-salary`;
      let url = `${apiBase}/data/all`;

      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) {
        if (dataType === 'bonus') {
          params.append('period', month);
        } else {
          params.append('month', month);
        }
      }
      if (genbaName) params.append('genbaName', genbaName);
      if (employeeName) params.append('employeeName', employeeName);

      if (params.toString()) {
        url = `${apiBase}/data?${params.toString()}`;
      }

      const response = await authenticatedFetch(url);
      const result = await response.json();

      if (result.success) {
        setData(result.data || []);
      } else {
        setError(result.message || 'データの読み込みに失敗しました。');
      }
    } catch (error: any) {
      setError('データの読み込み中にエラーが発生しました: ' + error.message);
      console.error('Data fetching error:', error);
    } finally {
      setLoading(false);
    }
  }, [dataType]);

  useEffect(() => {
    setData([]);
    fetchFilteredData();
  }, [fetchFilteredData]);

  return {
    data,
    loading,
    error,
    fetchFilteredData
  };
};
